import { IonToggle } from '@ionic/react';

import { FieldWrap, MaskedInput } from '~/components/forms/FormField';
import { SectionHead } from '~/components/ui/SectionHead';
import { formatBrlNumber, parseBrlInput } from '~/lib/masks';
import { useWizard } from '~/pages/emitir/wizardContext';
import { type ValorInput, valorSchema } from '~/schemas/invoice';

interface StepValorProps {
  fieldErrors: Record<string, string>;
}

interface RetencaoConfig {
  key: keyof ValorInput['retencoes'];
  aliquotaKey: keyof ValorInput['retencoes'];
  label: string;
  description: string;
}

const RETENCOES: readonly RetencaoConfig[] = [
  {
    key: 'irrf',
    aliquotaKey: 'irrf_aliquota',
    label: 'IRRF',
    description: 'Imposto de Renda Retido na Fonte',
  },
  {
    key: 'pis',
    aliquotaKey: 'pis_aliquota',
    label: 'PIS',
    description: 'Contribuição para o PIS',
  },
  {
    key: 'cofins',
    aliquotaKey: 'cofins_aliquota',
    label: 'COFINS',
    description: 'Contribuição para a Cofins',
  },
  {
    key: 'csll',
    aliquotaKey: 'csll_aliquota',
    label: 'CSLL',
    description: 'Contribuição Social sobre Lucro Líquido',
  },
  {
    key: 'inss',
    aliquotaKey: 'inss_aliquota',
    label: 'INSS',
    description: 'Contribuição Previdenciária',
  },
];

export function StepValor({ fieldErrors }: StepValorProps): JSX.Element {
  const { draft, setValor } = useWizard();
  const { valor } = draft;

  const totalRetencoes = RETENCOES.reduce((sum, item) => {
    if (!valor.retencoes[item.key]) return sum;
    const aliquota = valor.retencoes[item.aliquotaKey] as number;
    return sum + (valor.valor_servico * aliquota) / 100;
  }, 0);
  const valorIss = (valor.valor_servico * valor.iss_aliquota) / 100;
  const valorLiquido = valor.valor_servico - totalRetencoes;

  const handleValorChange = (raw: string): void => {
    const parsed = parseBrlInput(raw);
    setValor({ ...valor, valor_servico: parsed.numericValue });
  };

  const handleRetencaoToggle = (config: RetencaoConfig, checked: boolean): void => {
    setValor({
      ...valor,
      retencoes: { ...valor.retencoes, [config.key]: checked },
    });
  };

  const handleAliquotaChange = (config: RetencaoConfig, rawValue: string): void => {
    const sanitized = rawValue.replace(/[^\d.,]/g, '').replace(',', '.');
    const numeric = Number.parseFloat(sanitized);
    if (!Number.isFinite(numeric)) return;
    setValor({
      ...valor,
      retencoes: {
        ...valor.retencoes,
        [config.aliquotaKey]: Math.max(0, Math.min(100, numeric)),
      },
    });
  };

  const issAliquotaValue = Number.isFinite(valor.iss_aliquota) ? String(valor.iss_aliquota) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionHead label="Valor do serviço" />
      <div
        style={{
          background: 'var(--rx-surface)',
          border: '0.5px solid var(--rx-border)',
          borderRadius: 14,
          padding: '16px 16px 18px',
        }}
      >
        <FieldWrap id="valor-servico" label="Valor bruto (R$)" error={fieldErrors.valor_servico}>
          <MaskedInput
            id="valor-servico"
            value={valor.valor_servico > 0 ? formatBrlNumber(valor.valor_servico) : ''}
            onChange={handleValorChange}
            inputmode="numeric"
            placeholder="0,00"
          />
        </FieldWrap>

        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: '0.5px solid var(--rx-border)',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 10,
          }}
        >
          <Total label="Bruto" value={valor.valor_servico} tone="ink" />
          <Total label="ISS" value={valorIss} tone="warn" />
          <Total label="Líquido" value={valorLiquido} tone="brand" />
        </div>
      </div>

      <SectionHead label="ISS · Imposto sobre serviço" />
      <div
        style={{
          background: 'var(--rx-surface)',
          border: '0.5px solid var(--rx-border)',
          borderRadius: 14,
          padding: '16px 16px 18px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 14,
            alignItems: 'end',
          }}
        >
          <FieldWrap id="iss-aliquota" label="Alíquota (%)" error={fieldErrors.iss_aliquota}>
            <MaskedInput
              id="iss-aliquota"
              value={issAliquotaValue}
              onChange={(raw) => {
                const sanitized = raw.replace(/[^\d.,]/g, '').replace(',', '.');
                const numeric = Number.parseFloat(sanitized);
                if (!Number.isFinite(numeric)) return;
                setValor({ ...valor, iss_aliquota: Math.max(0, Math.min(100, numeric)) });
              }}
              inputmode="decimal"
            />
          </FieldWrap>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              paddingBottom: 8,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  color: 'var(--rx-text-3)',
                }}
              >
                Retido na fonte
              </div>
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--rx-text-2)' }}>
                Retido pelo tomador.
              </div>
            </div>
            <IonToggle
              className="rx-toggle"
              aria-label="ISS retido pelo tomador"
              checked={valor.iss_retido}
              onIonChange={(event) => setValor({ ...valor, iss_retido: event.detail.checked })}
            />
          </div>
        </div>
      </div>

      <SectionHead label="Retenções federais" action={<span className="rx-mono">opcional</span>} />
      <div
        style={{
          background: 'var(--rx-surface)',
          border: '0.5px solid var(--rx-border)',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        {RETENCOES.map((config, idx) => {
          const isActive = valor.retencoes[config.key] as boolean;
          const aliquota = valor.retencoes[config.aliquotaKey] as number;
          return (
            <div
              key={config.key}
              style={{
                padding: '14px 16px',
                borderTop: idx === 0 ? 'none' : '0.5px solid var(--rx-border)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className="rx-mono"
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      letterSpacing: 0,
                      color: 'var(--rx-text)',
                    }}
                  >
                    {config.label}
                  </div>
                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      color: 'var(--rx-text-2)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {config.description}
                  </div>
                </div>
                <IonToggle
                  className="rx-toggle"
                  checked={isActive}
                  onIonChange={(event) => handleRetencaoToggle(config, event.detail.checked)}
                />
              </div>

              {isActive && (
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '0.5px dashed var(--rx-border)',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <FieldWrap label="Alíquota (%)" id={`aliquota-${config.key}`}>
                    <MaskedInput
                      id={`aliquota-${config.key}`}
                      value={String(aliquota)}
                      onChange={(value) => handleAliquotaChange(config, value)}
                      inputmode="decimal"
                    />
                  </FieldWrap>
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase',
                        color: 'var(--rx-text-3)',
                      }}
                    >
                      Retido
                    </div>
                    <div
                      className="rx-mono"
                      style={{
                        marginTop: 4,
                        fontSize: 16,
                        fontWeight: 500,
                        letterSpacing: '-0.3px',
                        color: 'var(--rx-text)',
                      }}
                    >
                      R$ {formatBrlNumber((valor.valor_servico * aliquota) / 100)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface TotalProps {
  label: string;
  value: number;
  tone: 'brand' | 'warn' | 'ink';
}

const TONE_COLOR: Record<TotalProps['tone'], string> = {
  brand: 'var(--rx-brand)',
  warn: 'var(--rx-warn)',
  ink: 'var(--rx-text)',
};

function Total({ label, value, tone }: TotalProps): JSX.Element {
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
          color: 'var(--rx-text-3)',
        }}
      >
        {label}
      </div>
      <div
        className="rx-mono"
        style={{
          marginTop: 6,
          fontSize: 16,
          fontWeight: 600,
          letterSpacing: '-0.3px',
          color: TONE_COLOR[tone],
        }}
      >
        R$ {formatBrlNumber(value)}
      </div>
    </div>
  );
}

export function validateValor(value: ValorInput): Record<string, string> {
  const result = valorSchema.safeParse(value);
  if (result.success) return {};
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && !errors[key]) {
      errors[key] = issue.message;
    }
  }
  return errors;
}
